import React, { Suspense } from 'react';
import { Card, Divider } from 'wix-ui-tpa/cssVars';
import { CartModalDataHooks } from '../dataHooks';
import Text, { TextType } from '../../../Text/Text';
import DateAndTime from '../../DateAndTime/DateAndTime';
import { classes, st } from './ModalContent.st.css';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { FormSelectedSlot } from '../../../../../types/formSelectedSlots';
import { ServiceData } from '../../../../../utils/state/types';
import { getServiceSlotIdentifier } from '../../../../../utils';
import { getParticipantLabel } from '../../../../../utils/multiParticipantFlow';
import { getParticipantName } from '../../../../../utils/multiParticipantFlow/participants';
import { TagName } from '../../../../../utils/tagName.const';
import EmptyStatePage from '../../EmptyStatePage/EmptyStatePage';
import UpsellPlugin from '../../upsellPlugin';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';

const ServiceImage = React.lazy(
  () => import('../../ServiceImage/ServiceImage'),
);

export interface ModalContentProps {
  serviceData: ServiceData;
  formSelectedSlot: FormSelectedSlot;
}

export const ModalContent: React.FC<ModalContentProps> = ({
  serviceData,
  formSelectedSlot,
}) => {
  const { isRTL } = useEnvironment();
  const { t } = useTranslation();
  const { isUpsellPluginInstalled, participantFormValues } =
    useFormComponentContext();

  const firstNestedSlot = formSelectedSlot.nestedSlots[0];

  const serviceIdentifier = getServiceSlotIdentifier(firstNestedSlot);
  const slotService = serviceData.slotServices[serviceIdentifier];
  const service = slotService?.service;
  const serviceImage = service?.images?.[0];
  // Each participant is booked separately, so each one gets its own card.
  const participants = serviceData.isSingleService
    ? slotService?.participants ?? []
    : [];

  const renderCard = (participantName?: string) => (
    <Card
      data-hook={CartModalDataHooks.CARD}
      className={st(classes.card, { rtl: isRTL })}
    >
      {serviceImage && serviceData.isSingleService && (
        <Card.Container className={classes.left}>
          <ServiceImage
            {...serviceImage}
            dataHook={CartModalDataHooks.CARD_IMAGE}
          />
        </Card.Container>
      )}
      <Card.Container className={classes.right}>
        <div>
          <Text isBlackOnly data-hook={CartModalDataHooks.CARD_TITLE}>
            {serviceData.isSingleService
              ? service.name
              : t('app.bookings-form.cart-modal.services-text', {
                  numOfServices: formSelectedSlot.nestedSlots.length,
                })}
          </Text>
          <DateAndTime
            totalNumberOfSessions={1}
            slot={firstNestedSlot}
            service={service}
            isBlackOnly
          />
          {participantName && (
            <Text
              type={TextType.Secondary}
              tagName={TagName.Paragraph}
              data-hook={CartModalDataHooks.CARD_PARTICIPANT_NAME}
            >
              {participantName}
            </Text>
          )}
        </div>
      </Card.Container>
    </Card>
  );

  return (
    <Suspense fallback={<EmptyStatePage isProcessing />}>
      {participants.length > 1 ? (
        <div className={classes.cards}>
          {participants.map((participant, index) => (
            <React.Fragment key={participant.id}>
              {index > 0 && <Divider className={classes.divider} />}
              {renderCard(
                getParticipantName(participantFormValues?.[index]) ||
                  getParticipantLabel(t, index + 1),
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        renderCard()
      )}

      {isUpsellPluginInstalled && <UpsellPlugin />}
    </Suspense>
  );
};
